import { useState } from 'react';
import { FaCheck, FaTimes, FaTrash } from 'react-icons/fa';
import VerifyDialog from '../utils/VerifyDialog';

const ORDER_STATUS_STYLES = {
  Pending: { bg: 'bg-amber-100', text: 'text-amber-900', border: 'border-amber-300', dot: 'bg-amber-500' },
  Accepted: { bg: 'bg-emerald-100', text: 'text-emerald-900', border: 'border-emerald-300', dot: 'bg-emerald-600' },
  Rejected: { bg: 'bg-red-100', text: 'text-red-900', border: 'border-red-300', dot: 'bg-red-600' },
  Cancelled: { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-300', dot: 'bg-gray-500' },
  Open: { bg: 'bg-sky-100', text: 'text-sky-900', border: 'border-sky-300', dot: 'bg-sky-600' },
  Closed: { bg: 'bg-violet-100', text: 'text-violet-900', border: 'border-violet-300', dot: 'bg-violet-600' },
};

const QUOTE_STATUS_STYLES = {
  new: { bg: 'bg-blue-100', text: 'text-blue-900', border: 'border-blue-300', dot: 'bg-blue-600' },
  reviewed: { bg: 'bg-indigo-100', text: 'text-indigo-900', border: 'border-indigo-300', dot: 'bg-indigo-600' },
  quoted: { bg: 'bg-cyan-100', text: 'text-cyan-900', border: 'border-cyan-300', dot: 'bg-cyan-600' },
  closed: { bg: 'bg-violet-100', text: 'text-violet-900', border: 'border-violet-300', dot: 'bg-violet-600' },
  rejected: { bg: 'bg-red-100', text: 'text-red-900', border: 'border-red-300', dot: 'bg-red-600' },
};

const ORDER_WORKFLOW = ['Open', 'Closed', 'Cancelled'];

const ORDER_STATUS_CONFIRM = {
  Closed: {
    title: 'Close order',
    message: 'Are you sure you want to mark this order as closed?',
    warning: '📧 A thank-you email will be sent to the customer.',
  },
  Cancelled: {
    title: 'Cancel order',
    message: 'Are you sure you want to cancel this order?',
  },
};

const QUOTE_WORKFLOW = ['new', 'reviewed', 'quoted', 'closed'];

function StatusBadge({ status, variant = 'order' }) {
  const styles = variant === 'quote' ? QUOTE_STATUS_STYLES : ORDER_STATUS_STYLES;
  const key = variant === 'quote' ? (status || '').toLowerCase() : status;
  const s = styles[key] || { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-300', dot: 'bg-gray-400' };
  const label = variant === 'quote' ? (status || 'unknown') : status;

  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-semibold capitalize ${s.bg} ${s.text} ${s.border}`}
    >
      <span className={`h-2 w-2 rounded-full ${s.dot}`} aria-hidden="true" />
      {label}
    </span>
  );
}

function ActionButton({ children, onClick, disabled, variant = 'neutral', className = '' }) {
  const variants = {
    accept: 'bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-700',
    reject: 'bg-red-600 hover:bg-red-700 text-white border-red-700',
    primary: 'bg-candy-primary hover:bg-[#8a1f1c] text-white border-[#8a1f1c]',
    neutral: 'bg-white hover:bg-gray-50 text-gray-800 border-gray-300',
    danger: 'bg-red-800 hover:bg-red-900 text-white border-red-900',
    disabled: 'bg-gray-200 text-gray-500 border-gray-200 cursor-not-allowed',
  };
  const v = disabled ? variants.disabled : variants[variant] || variants.neutral;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex min-h-[44px] flex-1 sm:flex-none items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-semibold transition touch-manipulation ${v} ${className}`}
    >
      {children}
    </button>
  );
}

/** Cake / sweet treat orders */
export function OrderStatusPanel({
  status,
  onAccept,
  onReject,
  onStatusChange,
  onDelete,
  canDelete = false,
  busy = false,
}) {
  const [pendingAction, setPendingAction] = useState(null);
  const [confirmStatus, setConfirmStatus] = useState(null);
  const isPending = status === 'Pending';
  const canChangeWorkflow = !['Pending', 'Rejected'].includes(status);
  const workflowOptions = ORDER_WORKFLOW.filter((s) => s !== status);
  const confirmConfig = confirmStatus ? ORDER_STATUS_CONFIRM[confirmStatus] : null;

  const runAction = async (label, fn) => {
    if (busy || !fn) return;
    setPendingAction(label);
    try {
      await fn();
    } finally {
      setPendingAction(null);
    }
  };

  const handleWorkflowClick = (next) => {
    if (busy) return;
    if (ORDER_STATUS_CONFIRM[next]) {
      setConfirmStatus(next);
      return;
    }
    runAction(next, () => onStatusChange?.(next));
  };

  const handleConfirmStatusChange = async () => {
    const next = confirmStatus;
    if (!next) return;
    setConfirmStatus(null);
    await runAction(next, () => onStatusChange?.(next));
  };

  return (
    <div className="rounded-xl border border-candy-border bg-gradient-to-br from-[#fef9f9] to-white p-4 sm:p-5 shadow-sm print:hidden">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Order status</p>
          <p className="text-sm text-gray-600 mt-0.5">Update where this order is in your workflow</p>
        </div>
        <StatusBadge status={status} variant="order" />
      </div>

      {isPending ? (
        <div className="space-y-3">
          <p className="text-sm text-gray-700">This order is waiting for your decision.</p>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <ActionButton
              variant="accept"
              disabled={busy}
              onClick={() => runAction('accept', onAccept)}
              className="sm:flex-1"
            >
              <FaCheck className="shrink-0" />
              {pendingAction === 'accept' ? 'Accepting…' : 'Accept order'}
            </ActionButton>
            <ActionButton
              variant="reject"
              disabled={busy}
              onClick={() => runAction('reject', onReject)}
              className="sm:flex-1"
            >
              <FaTimes className="shrink-0" />
              {pendingAction === 'reject' ? 'Opening…' : 'Reject order'}
            </ActionButton>
          </div>
        </div>
      ) : canChangeWorkflow ? (
        <div className="space-y-3">
          <p className="text-sm text-gray-700">Move this order to:</p>
          <div className="flex flex-col sm:flex-row flex-wrap gap-2">
            {workflowOptions.map((next) => (
              <ActionButton
                key={next}
                variant={next === 'Cancelled' ? 'reject' : 'neutral'}
                disabled={busy || !!confirmStatus}
                onClick={() => handleWorkflowClick(next)}
              >
                {pendingAction === next ? `Updating…` : `Mark as ${next}`}
              </ActionButton>
            ))}
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-600 rounded-lg bg-gray-50 border border-gray-200 px-3 py-2">
          No further status changes available for <strong>{status}</strong> orders.
        </p>
      )}

      <div className="mt-4 pt-4 border-t border-candy-border/60 flex flex-col sm:flex-row sm:justify-end gap-2">
        <ActionButton
          variant="danger"
          disabled={!canDelete || busy || !!confirmStatus}
          onClick={onDelete}
          className="w-full sm:w-auto"
        >
          <FaTrash className="shrink-0" />
          Delete order
        </ActionButton>
      </div>

      <div onClick={(e) => e.stopPropagation()}>
        <VerifyDialog
          open={!!confirmStatus}
          title={confirmConfig?.title || 'Update status'}
          message={confirmConfig?.message || 'Are you sure you want to update this order?'}
          warning={confirmConfig?.warning}
          onConfirm={handleConfirmStatusChange}
          onCancel={() => setConfirmStatus(null)}
        />
      </div>
    </div>
  );
}

/** Quote request admin */
export function QuoteStatusPanel({
  status,
  onStatusChange,
  onDelete,
  canDelete = false,
  busy = false,
}) {
  const normalized = (status || '').toLowerCase();
  const [pendingStatus, setPendingStatus] = useState(null);

  const handleSelect = async (next) => {
    if (busy || next === normalized || !onStatusChange) return;
    setPendingStatus(next);
    try {
      await onStatusChange(next);
    } finally {
      setPendingStatus(null);
    }
  };

  return (
    <div className="rounded-xl border border-candy-border bg-gradient-to-br from-[#fef9f9] to-white p-4 sm:p-5 shadow-sm print:hidden">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Quote request status</p>
          <p className="text-sm text-gray-600 mt-0.5">Track progress from review to quoted</p>
        </div>
        <StatusBadge status={normalized} variant="quote" />
      </div>

      <p className="text-sm text-gray-700 mb-3">Select a status:</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {QUOTE_WORKFLOW.map((opt) => {
          const isCurrent = opt === normalized;
          const isLoading = pendingStatus === opt;
          const s = QUOTE_STATUS_STYLES[opt] || QUOTE_STATUS_STYLES.new;
          return (
            <button
              key={opt}
              type="button"
              disabled={busy || isCurrent}
              onClick={() => handleSelect(opt)}
              className={`min-h-[44px] rounded-lg border-2 px-3 py-2.5 text-sm font-semibold capitalize transition touch-manipulation ${
                isCurrent
                  ? `${s.bg} ${s.text} ${s.border} ring-2 ring-offset-1 ring-candy-primary/40`
                  : 'bg-white text-gray-700 border-gray-200 hover:border-candy-primary/50 hover:bg-red-50/50'
              } ${busy && !isLoading ? 'opacity-60' : ''}`}
            >
              {isLoading ? 'Saving…' : opt}
              {isCurrent && <span className="block text-[10px] font-normal mt-0.5 opacity-80">Current</span>}
            </button>
          );
        })}
        {normalized === 'rejected' && (
          <button
            type="button"
            disabled
            className={`min-h-[44px] rounded-lg border-2 px-3 py-2.5 text-sm font-semibold capitalize col-span-2 sm:col-span-4 ${QUOTE_STATUS_STYLES.rejected.bg} ${QUOTE_STATUS_STYLES.rejected.text} ${QUOTE_STATUS_STYLES.rejected.border}`}
          >
            rejected
            <span className="block text-[10px] font-normal mt-0.5 opacity-80">Current</span>
          </button>
        )}
      </div>

      {busy && !pendingStatus && (
        <p className="text-xs text-gray-500 mt-2 italic">Saving status…</p>
      )}

      <div className="mt-4 pt-4 border-t border-candy-border/60 flex flex-col sm:flex-row sm:justify-end gap-2">
        <ActionButton
          variant="danger"
          disabled={!canDelete || busy}
          onClick={onDelete}
          className="w-full sm:w-auto"
        >
          <FaTrash className="shrink-0" />
          Delete quote request
        </ActionButton>
      </div>
    </div>
  );
}

export default OrderStatusPanel;
